import { Button, DialogContent, Stack, Typography, DialogTitle, useMediaQuery, useTheme } from "@mui/material";
import { useEffect, useState } from "react";
import { Markdown } from "@/app/features/shared/markdown/Markdown";
import Dialog from "@mui/material/Dialog";
import FormBuilder from "@/app/features/shared/form-builder/ui/FormBuilder";
import { FormField, FormState } from "@/app/features/shared/form-builder/types/types";
import { defaultFormState } from "@/app/features/shared/form-builder/types/defaults";
import {
    RegisterNewsletterFormData
} from "@/app/features/sites/header/ui/desktop/flyout-info/flyouts/types/newsletter/form-data";
import { sendEditors } from "@/app/lib/actions/sendEditors";
import { RegisterNewsletterFormSchema } from "@/app/features/sites/header/ui/desktop/flyout-info/flyouts/types/newsletter/form-schema";
import DialogActions from "@mui/material/DialogActions";
import useIsDesktop from "@/hooks/useDesktop";
import { signatureBorderStyle } from "@/app/theme/theme-tokens";

const preText = `
Mit dem Eintrag in unseren Newsletter-Verteiler erklären Sie sich einverstanden, dass das Museum Erding Ihre angegebenen Kontaktdaten verarbeitet, um Sie über interessante Veranstaltungen und Aktionen im Museum Erding zu informieren. 
Der Newsletter-Versand erfolgt entsprechend unserer [Datenschutzerklärung](/privacy).
`;
const registerFields: FormField[] = [
    {
        type: "text",
        name: "email",
        label: "E-Mail Adresse",
        required: true,
    },
    {
        type: "text",
        name: "firstName",
        label: "Vorname",
    },
    {
        type: "text",
        name: "lastName",
        label: "Nachname",
    },
    {
        type: "checkbox",
        name: "termsAndConditionsAccepted",
        label: "Ich habe die Datenschutzbestimmungen gelesen und erkenne diese ausdrücklich an.",
        required: true,
    },
]

const defaultFormData: RegisterNewsletterFormData = {
    email: "",
    firstName: "",
    lastName: "",
    termsAndConditionsAccepted: false,
}

export default function NewsletterRegisterDialog() {

    const [isRegisterModalOpen, setIsRegisterModalOpen] = useState(false);
    const [isSuccessDialogOpen, setIsSuccessDialogOpen] = useState(false);
    const [formData, setFormData] = useState<RegisterNewsletterFormData>(defaultFormData)
    const [formState, setFormState] = useState<FormState>(defaultFormState)

    const isDesktop = useIsDesktop();

    useEffect(() => {
        if (!isRegisterModalOpen) {
            setFormData(defaultFormData)
            setFormState(defaultFormState)
        }
    }, [isRegisterModalOpen]);

    useEffect(() => {
        const validated = RegisterNewsletterFormSchema.safeParse(formData);

        if (!validated.success) {
            setFormState((prev: FormState) => ({
                ...prev,
                fieldErrors: validated.error.formErrors.fieldErrors,
                isValid: false
            }));
        } else {
            setFormState((prev: FormState) => ({
                ...prev,
                fieldErrors: null,
                isValid: true
            }));
        }
    }, [formData, setFormState]);

    const onStartRegister = () => {
        setIsRegisterModalOpen(true);
    }

    const onRegisterFormSubmit = async () => {
        setFormState((prev: FormState) => ({
            ...prev,
            isSubmitting: true
        }));
        const message = [
            '## Anmeldung zum Newsletter',
            '',
            'Der Besucher hat darum gebeten in den Newsletterverteiler aufgenommen zu werden.',
            '',
            '### Formulardaten:',
            `- **E-Mail:** ${formData.email}`,
            `- **Vorname:** ${formData.firstName || 'n/a'}`,
            `- **Nachname:** ${formData.lastName || 'n/a'}`,
            `- **Datenschutzerklärung akzeptiert:** ${formData.termsAndConditionsAccepted ? 'Ja' : 'Nein'}`,
        ].join('\r\n'); // normalize to CRLF for better email client compatibility

        try {
            await sendEditors({
                subject: "💌 Anmeldung zum Newsletter",
                email: "peter.c.glade@googlemail.com",
                message
            });

            setFormState({
                ...formState,
                success: true
            })

            setIsSuccessDialogOpen(true);
        } catch (error) {
            console.error('Error sending editors mail: ', JSON.stringify(error))
            const errorText = "Das hat leider nicht geklappt, bitte versuchen Sie es später noch einmal oder melde sich direkt unter museum@erding.de";
            setFormState({
                ...formState,
                error: errorText,
                success: false
            })

        } finally {
            setFormState((prev: FormState) => ({
                ...prev,
                isSubmitting: false
            }));
        }
    }

    const resetForm = () => {
        setFormData(defaultFormData)
        setFormState(defaultFormState)
        setIsRegisterModalOpen(false);
        setIsSuccessDialogOpen(false);
    }

    return (
        <>
            <Button
                variant="outlined"
                size="large"
                onClick={onStartRegister}
                fullWidth
            >
                Jetzt anmelden
            </Button>

            <Dialog
                open={isRegisterModalOpen}
                onClose={resetForm}
                fullScreen={!isDesktop}
                fullWidth
                maxWidth="sm"
                scroll="paper"
                sx={{
                    ...(isDesktop ? signatureBorderStyle : {})
                }}
                PaperProps={{
                    sx: {
                        mx: !isDesktop ? 0 : { xs: 1, sm: 0 }
                    }
                }}
            >
                <DialogTitle>
                    <Typography
                        variant="h2"
                        component="span"
                        color="text.secondary"
                        className="px-6 pt-6 text-center"
                    >
                        Anmeldung zum Newsletter
                    </Typography>
                </DialogTitle>
                <DialogContent>
                    <Stack spacing={2}>
                        <Markdown  value={preText}/>
                        <FormBuilder
                            key={isRegisterModalOpen ? 'open' : 'closed'}
                            purpose="send"
                            fields={registerFields}
                            formState={formState}
                            formData={formData}
                            setFormData={(data) => setFormData((prev) => ({ ...prev, ...data }))}
                            onSubmit={() => onRegisterFormSubmit()}
                            onCancel={resetForm}
                            disableSaveAndClose
                        />
                    </Stack>
                </DialogContent>
            </Dialog>

            <SuccessDialog isOpen={isSuccessDialogOpen} onApprove={resetForm} />
        </>
    );
}

function SuccessDialog({ isOpen = false, onApprove }: { isOpen: boolean, onApprove: () => void }) {
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
    return (
        <Dialog open={isOpen} fullScreen={fullScreen} fullWidth maxWidth="sm" scroll="paper" PaperProps={{ sx: { mx: fullScreen ? 0 : { xs: 1, sm: 0 } } }}>
            <DialogTitle>
                <Typography
                    variant="h1"
                    color="text.secondary"
                    className="px-6 pt-6 text-center"
                >
                    Erfolgreich angemeldet! 🎉
                </Typography>
            </DialogTitle>
            <DialogContent>
                <Markdown
                    value={`              
Vielen Dank für Ihre Anmeldung zum Newsletter des Museum Erding. Wir werden Ihre Anfrage so schnell wie möglich bearbeiten. 
Anschließend erhalten Sie spannende Neuerungen, aktuelle Veranstaltungshinweise und exklusive Einblicke direkt in Ihr Postfach.

**Wir freuen uns, dass Sie dabei sind!**
                    `}
                />
            </DialogContent>
            <DialogActions>
                <Button
                    variant="contained"
                    onClick={onApprove}
                >
                    Ok
                </Button>
            </DialogActions>
        </Dialog>
    )
}
